import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRouter } from 'next/navigation';
import { useVisibleFieldDefinitions } from '@frameio/assets/shared';
import { useProjectWorkspaceInfoFragment } from '@frameio/graphql-client/generated';
import { number, plural } from '@frameio/i18n';
import { useMemberAccessFlow } from '@frameio/invites/hooks';
import {
  isValidTreeItemType,
  ProjectHeaderMenuItem,
  ProjectHeaderMenuTrigger,
  ProjectTree,
  type ProjectTreeItem,
  useProjectTree,
} from '@frameio/project-tree';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useIsIntegrationClient } from '@frameio/shared/contexts/ClientNameContext';
import { AppDownloadDialog } from '@frameio/shared/resources/mounted-storage';
import {
  getLink,
  useCurrentPageType,
  type ValidHref,
} from '@frameio/shared/routing';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { appComponentsStorage } from '@frameio/shared/storage';
import {
  getProjectGradient,
  LoadingContainer,
  Menu,
  useMeasure,
} from '@frameio/vapor';
import { ProjectSettingsModal } from '@frameio/workspaces/settings/components';
import { DuplicateProjectModal } from '@frameio/workspaces/settings/components/DuplicateProjectModal';
import { useDeleteProjectConfirmationModal } from '@frameio/workspaces/settings/hooks';
import { useAccountPromotionalBannerOptOut } from '../../../../hooks/useAccountPromotionalBannerOptOut';
import { ContextMenu } from '../ContextMenu';
import { DriveFloatingToast } from '../DriveFloatingToast';
import { ProjectMenu } from '../ProjectMenu';
import { useProjectNavigationActivation } from '../ProjectNavActivationManager';
import TEXT from './generated/TEXT/StaticAssets';
import styles from './StaticProjectNav.module.css';

const DRIVE_FLOATING_TOAST_DISMISSED_KEY =
  'frame-io--drive-floating-toast-dismissed';
const DRIVE_FLOATING_TOAST_TOOLTIP_ID = 'drive-floating-toast';

type Props = {
  projectId: string;
  rootFolderId?: string;
};

export function StaticAssets({ projectId, rootFolderId }: Props) {
  const pageType = useCurrentPageType();
  const { activate, state: activeId } = useProjectNavigationActivation();
  const isIntegrationClient = useIsIntegrationClient();

  const [isMenuShowing, setIsMenuShowing] = useState(false);
  const [isSettingsShowing, setIsSettingsShowing] = useState(false);
  const [isDuplicateShowing, setIsDuplicateShowing] = useState(false);
  /**
   * can't just rely on the hover state to show the blur background since
   * the header loses hover when the menu is opened. this state var keeps the
   * blur background visible when the user closes the menu but still hovers
   * the header area.
   */
  const [forceBackground, setForceBackground] = useState(false);

  const [isDriveToastDismissed, setIsDriveToastDismissed] = useState(
    () => appComponentsStorage.get(DRIVE_FLOATING_TOAST_DISMISSED_KEY) === true
  );
  const [isAppDownloadDialogShowing, setIsAppDownloadDialogShowing] =
    useState(false);

  const track = useTrackUnsafe();

  const isAccountOptedOut = useAccountPromotionalBannerOptOut();
  const isDriveToastVisible =
    !isAccountOptedOut && !isDriveToastDismissed && !isIntegrationClient;

  useEffect(() => {
    if (isDriveToastVisible) {
      track('onboarding-tooltip-displayed', {
        tooltip_id: DRIVE_FLOATING_TOAST_TOOLTIP_ID,
      });
    }
  }, [isDriveToastVisible, track]);

  const handleDriveToastDismiss = useCallback(() => {
    setIsDriveToastDismissed(true);
    appComponentsStorage.set(DRIVE_FLOATING_TOAST_DISMISSED_KEY, true);
    track('onboarding-tooltip-dismissed', {
      tooltip_id: DRIVE_FLOATING_TOAST_TOOLTIP_ID,
    });
  }, [track]);

  const handleDriveToastCtaClick = useCallback(() => {
    setIsAppDownloadDialogShowing(true);
    setIsDriveToastDismissed(true);
    appComponentsStorage.set(DRIVE_FLOATING_TOAST_DISMISSED_KEY, true);
  }, []);

  const [headerRef, headerRect] = useMeasure();
  const [driveToastRef, driveToastRect] = useMeasure();

  const { data: project } = useProjectWorkspaceInfoFragment(projectId);

  const selectedItem = useMemo<ProjectTreeItem | undefined>(() => {
    const [itemType, itemId] = activeId.split(':');

    if (itemType === 'share' || itemType === 'all-shares') {
      return undefined;
    }

    if (!isValidTreeItemType(itemType)) return undefined;

    return {
      id: itemId,
      type: itemType,
    };
  }, [activeId]);

  const { fetchMore, loading, toggleItem, tree } = useProjectTree(
    rootFolderId,
    pageType === 'trash',
    selectedItem
  );

  const router = useRouter();

  const handleItemSelect = (item: ProjectTreeItem, href?: ValidHref) => {
    if (!href) return;

    activate(item.id, item.type, () => {
      router.push(href);
    });
  };

  const visibleFieldDefinitions = useVisibleFieldDefinitions();

  const isLoading =
    loading ||
    rootFolderId === undefined ||
    visibleFieldDefinitions === undefined;

  const accountId = useActiveAccountId();
  const { renderMemberAccessButton, renderedMemberAccessDialog } =
    useMemberAccessFlow({
      accountId,
      isDialogShowingInitially: false,
      label: TEXT.manageMembers,
      resourceId: projectId,
      trackingSource: 'ProjectHeaderMenu',
    });

  const avatarUrl = project?.images?.image64 ?? '';
  const memberCount =
    project?.uniqueMemberCount != null
      ? TEXT.memberCount(project.uniqueMemberCount)
      : '';
  const projectHeaderName = project?.name ?? '';

  const { deleteProjectModal, showDeleteProjectConfirmationModal } =
    useDeleteProjectConfirmationModal({
      onSuccess() {
        router.push(
          getLink({
            page: 'workspaces',
          })
        );
      },
    });
  const menuWidth = `${headerRect.width - PROJECT_HEADER_MENU_OFFSET * 3}px`;

  const headerBlurStyles = useMemo<CSSProperties | undefined>(() => {
    if (project.images?.image64) {
      return {
        [PROJECT_HEADER_BLUR_VAR]: `url(${project.images.image64})`,
      } as CSSProperties;
    }

    // getProjectGradient returns a RuleSet for styled-components, but with the
    // move to CSS modules, we have to pull out the 3rd entry
    const gradient = getProjectGradient(projectId)[2]?.toString();

    if (!gradient) {
      return undefined;
    }

    // the string has a special format for styled-components and needs slicing.
    // it should look like this:
    //
    // ";background-image:url(https://next.frame.io/images/project-cards/project-card-05@1x.jpg);"
    const gradientUrl = gradient.slice(
      gradient.indexOf('url('),
      gradient.indexOf(');') + 1
    );

    return {
      [PROJECT_HEADER_BLUR_VAR]: gradientUrl,
    } as CSSProperties;
  }, [project.images?.image64, projectId]);

  return (
    <>
      <div className={styles.assetsContainer}>
        <LoadingContainer
          className={styles.assetsLoading}
          isLoading={isLoading}
          loadingLabel={TEXT.loadingAssets}
        >
          <Menu
            aria-label={TEXT.projectMenuLabel}
            content={
              <>
                <ProjectHeaderMenuItem
                  avatarSize={PROJECT_HEADER_AVATAR_SIZE}
                  avatarUrl={avatarUrl}
                  details={memberCount}
                  projectId={projectId}
                  projectName={projectHeaderName}
                />
                {rootFolderId && (
                  <ProjectMenu
                    onDeletePress={() => {
                      if (!project.id || !project.name) return;

                      showDeleteProjectConfirmationModal([
                        {
                          id: project.id,
                          name: project.name,
                        },
                      ]);
                    }}
                    onDuplicatePress={() => {
                      setIsDuplicateShowing(true);
                    }}
                    onProjectSettingsPress={() => {
                      setIsSettingsShowing(true);
                    }}
                    renderMemberAccessButton={renderMemberAccessButton}
                    rootFolderId={rootFolderId}
                  />
                )}
              </>
            }
            isShowing={isMenuShowing}
            maxWidth={menuWidth}
            offset={[
              PROJECT_HEADER_MENU_OFFSET,
              PROJECT_HEADER_MENU_OFFSET - headerRect.height,
            ]}
            onRequestChange={(isShowing) => {
              setIsMenuShowing(isShowing);
              setForceBackground(isShowing);
            }}
            overflowPadding={PROJECT_HEADER_MENU_OFFSET}
            placement="bottom-start"
            shouldAnimate={false}
            shouldUsePortal={true}
            unmountDuration={0}
            width={menuWidth}
          >
            <ProjectHeaderMenuTrigger
              ref={headerRef}
              avatarSize={PROJECT_HEADER_AVATAR_SIZE}
              avatarUrl={avatarUrl}
              className={styles.projectHeader}
              data-show-blur={isMenuShowing || forceBackground}
              details={memberCount}
              isForcedVisible={forceBackground}
              onMouseLeave={() => {
                if (!isMenuShowing) {
                  setForceBackground(false);
                }
              }}
              projectId={projectId}
              projectName={projectHeaderName}
              style={headerBlurStyles}
            />
          </Menu>

          {renderedMemberAccessDialog}

          <div
            className={styles.treeContainer}
            style={
              !isAccountOptedOut &&
              !isDriveToastDismissed &&
              !isIntegrationClient
                ? ({
                    '--drive-floating-toast-height': `${driveToastRect.height}px`,
                  } as CSSProperties)
                : undefined
            }
          >
            {!isLoading && (
              <ProjectTree
                dndNamespace="project-nav"
                enableHiddenLinks={true}
                onBoundarySeen={fetchMore}
                onItemSelect={handleItemSelect}
                rootFolderId={rootFolderId}
                selectedItem={selectedItem}
                toggleItem={toggleItem}
                tree={tree}
              />
            )}
          </div>
          <ContextMenu
            rootFolderId={rootFolderId ?? ''}
            toggleItem={toggleItem}
          />
        </LoadingContainer>

        {isDriveToastVisible && (
          <div ref={driveToastRef} className={styles.driveFloatingToast}>
            <DriveFloatingToast
              onCtaClick={handleDriveToastCtaClick}
              onDismiss={handleDriveToastDismiss}
            />
          </div>
        )}
      </div>

      {deleteProjectModal}

      <AppDownloadDialog
        isShowing={isAppDownloadDialogShowing}
        onClose={() => setIsAppDownloadDialogShowing(false)}
        projectId={projectId}
        trackingSource="project_navigation"
      />

      <DuplicateProjectModal
        accountId={accountId}
        isShowing={isDuplicateShowing}
        onRequestClose={() => {
          setIsDuplicateShowing(false);
        }}
        projectId={projectId}
        workspaceId={project.workspace?.id ?? ''}
      />

      <ProjectSettingsModal
        accountId={accountId}
        isShowing={isSettingsShowing}
        onRequestClose={() => {
          setIsSettingsShowing(false);
        }}
        projectId={projectId}
        trackingSource="ProjectNavProjectMenu"
        workspaceId={project.workspace?.id ?? ''}
      />
    </>
  );
}
const PROJECT_HEADER_AVATAR_SIZE = 36;
const PROJECT_HEADER_MENU_OFFSET = 3;
const PROJECT_HEADER_BLUR_VAR = '--project-nav-header-image';

TEXT.DEFINE({
  /** Aria-label for assets section loading state */
  loadingAssets: 'Loading assets',
  /** Label for the manage members option for Project Menu */
  manageMembers: 'Manage Members',
  /** Number of members in this project for the ProjectHeader */
  memberCount: (count: number): string =>
    plural(count, `${number(count)} Members`, '1 Member'),
  /** Accessible name for the project header's menu dialog */
  projectMenuLabel: 'Project menu',
});
